// src/components/PieChart.js
import { useRef } from "react";
import { Chart as Chartjs, registerables } from 'chart.js';
import { Scatter } from "react-chartjs-2";
import PropTypes from "prop-types";
import moment from "moment";
import 'chartjs-adapter-moment';
import zoomPlugin from "chartjs-plugin-zoom";

import { event_types } from "components/Charts/DeviceEvents/event_types";
import { useSoftUIController } from "hooks/context";

Chartjs.register(...registerables);
Chartjs.register(zoomPlugin); // REGISTER PLUGIN

var timeout, current_chart;

function getLabelTimes(tooltipItems) {
    var index = tooltipItems[0].dataIndex;
    var next_index = index + 1 == tooltipItems[0].dataset.data.length ? index : index + 1;

    var first = moment(tooltipItems[0].dataset.data[index].x).format("LLL");
    var last = moment(tooltipItems[0].dataset.data[next_index].x).format("LLL");

    return { first, last };
}

function getTooltipTotalCount(tooltipItems) {
    var totalCount = 0;

    for (var item of tooltipItems) {
        totalCount += item.raw.data.count;
    }

    return totalCount;
}

function ScatterChart({ chartData = [], advancedToggle, advanced_labels, startTime, endTime, groupSize, setStartTime, setEndTime }) {

    const chartRef = useRef(null);
    const [controller, dispatch] = useSoftUIController();
    const { deviceLayout } = controller;

    const scrollEvent = (chart) => {
        // console.log(timeout);
        current_chart = chart;
        if (typeof timeout != 'undefined') {
            clearTimeout(timeout);
            timeout = undefined;
        }

        timeout = setTimeout(() => {
            runWheelScrollEvent(current_chart);
            timeout = undefined;
        }, 200);
    }

    const runWheelScrollEvent = ({ chart }) => {
        var start_time, end_time, group_size;
        let min_limit = moment(0)
        let max_limit = moment();
        // console.log(chart.scales.x.min)
        let min_moment = moment(chart.scales.x.min);
        let max_moment = moment(chart.scales.x.max);

        if (min_moment.isBefore(min_limit)) {
            start_time = min_limit;
        }
        else {
            start_time = min_moment;
        }

        if (max_moment.isAfter(max_limit)) {
            end_time = max_limit;
        }
        else {
            end_time = max_moment;
        }

        setStartTime(start_time);
        setEndTime(end_time);

    };

    var options = {
        layout: {
            padding: 8
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 0
        },
        plugins: {
            zoom: {
                zoom: {
                    mode: 'x',
                    drag: {
                        enabled: true,
                    },
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true,
                    },
                    speed: 0.5, sensitivity: 0.5,
                    onZoomComplete: scrollEvent,
                },
                limits: {
                    x: {
                        min: 1725112800 * 1000, // 1st September 2024
                        max: moment().unix() * 1000,
                        minRange: 16000 * 1000,
                    }
                }
            },
            legend: {
                labels: {
                    generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        const {
                            labels: {
                                usePointStyle,
                                pointStyle,
                                textAlign,
                                color
                            }
                        } = chart.legend.options;

                        return chart._getSortedDatasetMetas().map((meta) => {
                            const style = meta.controller.getStyle(usePointStyle ? 0 : undefined);

                            try {
                                const { chartArea: { top, bottom, height }, scales: { x, y } } = chart;

                                var event_type = event_types.find((row) => row.text == datasets[meta.index].label);
                                var labelFill = typeof event_type != 'undefined' ? event_type.colour : "black";
                            }
                            catch (e) {
                                var labelFill = "black";
                            }

                            return {
                                text: datasets[meta.index].label,
                                fillStyle: labelFill.replace(')', ', 0.7)').replace('rgb', 'rgba'),
                                fontColor: color,
                                hidden: false,
                                lineCap: style.borderCapStyle,
                                lineDash: style.borderDash,
                                lineDashOffset: style.borderDashOffset,
                                lineJoin: style.borderJoinStyle,
                                lineWidth: 1,
                                strokeStyle: labelFill,
                                pointStyle: pointStyle || style.pointStyle,
                                rotation: style.rotation,
                                textAlign: textAlign || style.textAlign,
                                borderRadius: 0,
                                datasetIndex: meta.index
                            };
                        }, this);
                    }
                }
            },
            title: {
                display: false,
                text: 'Event chart'
            },
            elements: {
                point: {
                    radius: 4,
                    hoverRadius: 5
                }
            },
            tooltip: {
                borderWidth: 3,
                callbacks: {
                    title: (tooltipItems) => {
                        return tooltipItems[0].dataset.label;
                    },
                    afterTitle: (tooltipItems) => {
                        var event = advanced_labels.find((e) => e.text == tooltipItems[0].dataset.label);
                        if (event?.level < 2) {
                            if (event?.desc) return event.desc;

                            return tooltipItems?.[0]?.raw?.data?.string ? tooltipItems?.[0]?.raw?.data?.string.split(/(?<!\/)\/(?!\/)/) : "";
                        }
                    },
                    beforeBody: () => { return ' ' },
                    label: () => { return false },
                    afterBody: (tooltipItems) => {
                        var totalCount = getTooltipTotalCount(tooltipItems);

                        for (var item of tooltipItems) {
                            totalCount += item.raw.data.count;
                        }

                        return 'Total Count: ' + totalCount;
                    },
                    beforeFooter: () => { return ' ' },
                    footer: (tooltipItems) => {
                        var { first, last } = getLabelTimes(tooltipItems);
                        var totalCount = getTooltipTotalCount(tooltipItems);

                        // if (first == last || totalCount == 1) {
                        //     return first;
                        // }
                        return 'From: ' + first;
                    },
                    // afterFooter: (tooltipItems) => {
                    //     var { first, last } = getLabelTimes(tooltipItems);
                    //     var totalCount = getTooltipTotalCount(tooltipItems);

                    //     if (first == last || totalCount == 1) {
                    //         return false;
                    //     }
                    //     return 'To: ' + last;
                    // }
                },
                borderColor: (tooltipItem) => {
                    return tooltipItem.tooltip.labelColors[0].borderColor;
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                min: startTime,
                max: endTime
            },
            y: {
                type: 'linear',
                position: 'left',
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    min: 0,
                    max: advanced_labels.length,
                    callback: (context, index) => {
                        var arr = advanced_labels;
                        if (index < arr.length) {
                            return (deviceLayout == 'mobile' ? "" : arr[index].text);
                        }
                    }
                }
            }
        }
    };

    var scatterChart = (
        <Scatter
            ref={chartRef}
            data={chartData.data}
            options={options}
        />
    );

    if (scatterChart.ref.current) {
        for (var scale in scatterChart.ref.current.scales) {
            if (scale.substring(0, 1) == 'y') {
                for (var index in scatterChart.ref.current.scales[scale].ticks) {
                    scatterChart.ref.current.scales[scale].ticks[index].value = index;
                }
            }
        }
    }

    return scatterChart;
};

// Typechecking props for the ScatterChart
ScatterChart.propTypes = {
    chartData: PropTypes.object,
    advancedToggle: PropTypes.bool,
    advanced_labels: PropTypes.array,
    startTime: PropTypes.instanceOf(moment()),
    setStartTime: PropTypes.func,
    endTime: PropTypes.instanceOf(moment()),
    setEndTime: PropTypes.func,
    groupSize: PropTypes.number,
};


export default ScatterChart;